import React, { useState, useEffect } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { useClickOutside } from "../../Hooks/useClickOutside";
import { Globe } from "lucide-react";

const SubCategoryDetailsModal = ({ isOpen, subCategory, onClose }) => {
  const [animate, setAnimate] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");
  
  // Language configuration
  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "hi", name: "Hindi", flag: "🇮🇳" },
    { code: "es", name: "Spanish", flag: "🇪🇸" },
    { code: "zh", name: "Chinese", flag: "🇨🇳" }
  ];

  // Helper to get multilingual value
  const getMultilingualValue = (value, lang) => {
    if (!value) return "";
    
    if (typeof value === "string") {
      return value;
    }
    
    if (typeof value === "object") {
      const fallback = Object.values(value)[0] || "";
      return value[lang] || value[lang + "Name"] || fallback;
    }
    
    return "";
  };

  // Get name in current language
  const getCurrentName = () => {
    return getMultilingualValue(subCategory?.name, activeLanguage) || "Unnamed";
  };

  // Get description in current language
  const getCurrentDescription = () => {
    return getMultilingualValue(subCategory?.description, activeLanguage) || "No description available";
  };

  // Get category name in current language
  const getCurrentCategoryName = () => {
    if (!subCategory?.category?.name) return "";
    return getMultilingualValue(subCategory.category.name, activeLanguage);
  };

  // Close with fade-out animation
  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  // Handle click outside
  const domNode = useClickOutside(() => handleClose());

  // Animate modal entrance
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    }
  }, [isOpen]);

  if (!isOpen || !subCategory) return null;

  return (
    <section
      className={`fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        ref={domNode}
        className={`bg-white rounded-xl shadow-lg w-[90%] md:w-[700px] max-h-[90vh] overflow-y-auto relative p-6 transform transition-all duration-300 ${
          animate ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-red-500 transition"
        >
          {icons.closeIcon}
        </button>

        {/* Language Selector */}
        <div className="mb-6 border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">View in:</span>
            </div>
            <div className="flex gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setActiveLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    activeLanguage === lang.code
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Image */}
          <div className="flex-shrink-0">
            <img
              src={
                subCategory.imgUrl ||
                "https://via.placeholder.com/200x200.png?text=No+Image"
              }
              alt={getCurrentName()}
              className="w-64 h-64 object-cover rounded-lg shadow-md"
            />
          </div>

          {/* Details */}
          <div className="flex-1">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-500">
                  {languages.find(l => l.code === activeLanguage)?.flag} 
                  {languages.find(l => l.code === activeLanguage)?.name}
                </span>
              </div>
              <h2 className="text-2xl font-philosopher text-gray-800 capitalize">
                {getCurrentName()}
              </h2>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Description:</h3>
              <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200">
                {getCurrentDescription()}
              </p>
            </div>

            {/* Category */}
            {getCurrentCategoryName() && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Parent Category:</h3>
                <div className="flex items-center gap-2">
                  {subCategory.category?.imgUrl && (
                    <img
                      src={subCategory.category.imgUrl}
                      alt={getCurrentCategoryName()}
                      className="w-10 h-10 object-cover rounded-md"
                    />
                  )}
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium">
                    {getCurrentCategoryName()}
                  </span>
                </div>
              </div>
            )}

            {/* Additional Info */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Created At:</h4>
                <p className="text-gray-700">
                  {new Date(subCategory.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Updated At:</h4>
                <p className="text-gray-700">
                  {new Date(subCategory.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] transition-all font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </section>
  );
};

export default SubCategoryDetailsModal;





// import React, { useState, useEffect } from "react";
// import { icons } from "../../../Assets/Icons/icons";
// import { useClickOutside } from "../../Hooks/useClickOutside";

// const SubCategoryDetailsModal = ({ isOpen, subCategory, onClose }) => {
//   const [animate, setAnimate] = useState(false);

//   // Close with fade-out animation
//   const handleClose = () => {
//     setAnimate(false);
//     setTimeout(() => {
//       onClose();
//     }, 250);
//   };

//   // Handle click outside
//   const domNode = useClickOutside(() => handleClose());

//   // Animate modal entrance
//   useEffect(() => {
//     if (isOpen) {
//       setTimeout(() => setAnimate(true), 10);
//     }
//   }, [isOpen]);

//   if (!isOpen || !subCategory) return null;

//   return (
//     <section
//       className={`fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 transition-opacity duration-300 ${
//         animate ? "opacity-100" : "opacity-0"
//       }`}
//     >
//       <div
//         ref={domNode}
//         className={`bg-white rounded-xl shadow-lg w-[90%] md:w-[600px] max-h-[90vh] overflow-y-auto relative p-6 transform transition-all duration-300 ${
//           animate ? "scale-100 opacity-100" : "scale-95 opacity-0"
//         }`}
//       >
//         {/* Close button */}
//         <button
//           onClick={handleClose}
//           className="absolute top-4 right-4 text-gray-600 hover:text-red-500 transition"
//         >
//           {icons.closeIcon}
//         </button>

//         {/* Content */}
//         <div className="flex flex-col items-center gap-4">
//           <img
//             src={
//               subCategory.imgUrl ||
//               "https://via.placeholder.com/200x200.png?text=No+Image"
//             }
//             alt={subCategory.name}
//             className="w-48 h-48 object-cover rounded-lg shadow-sm"
//           />
//           <h2 className="text-2xl font-philosopher text-gray-800 capitalize">
//             {subCategory.name}
//           </h2>
//           <p className="text-gray-500 text-center leading-relaxed">
//             {subCategory.description || "No description available."}
//           </p>

//           {subCategory.category?.name && (
//             <div className="mt-2">
//               <span className="px-4 py-1 bg-blue-100 text-blue-600 text-sm rounded-full">
//                 Category: {subCategory.category.name}
//               </span>
//             </div>
//           )}
//         </div>

//         {/* Footer */}
//         <div className="mt-6 flex justify-end">
//           <button
//             onClick={handleClose}
//             className="px-4 py-2 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] transition-all"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default SubCategoryDetailsModal;
